// Shared training-resource card for all six Resources areas.
// Supplementary resources: compact title-first rows. Primary (key) resources: prominent gold card with a clear action.
// Media loads only when opened; no autoplay; "Open original" always available.
import { createContext, useContext, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ExternalLink, Image as ImageIcon, PlayCircle, FileText, Presentation, Sheet, Link2, ZoomIn, ZoomOut, Music, ChevronRight, Folder, Loader2 } from "lucide-react";

const ICONS = { image: ImageIcon, video: PlayCircle, audio: Music, pdf: FileText, doc: FileText, slides: Presentation, sheet: Sheet, folder: Folder };
const KIND_LABEL = { image: "Image", video: "Video", audio: "Audio", pdf: "PDF", doc: "Document", slides: "Slides", sheet: "Spreadsheet", file: "File", link: "Link", folder: "Folder" };
const ACTION = { image: "View image", video: "Watch video", audio: "Listen to audio", pdf: "Read guide", doc: "Read guide", slides: "View slides", sheet: "Open spreadsheet", folder: "Open folder", file: "Open file", link: "Open link" };
const PREVIEWABLE = new Set(["video", "audio", "pdf"]);
const MEDIA = new Set(["video", "audio", "image"]);
const thumb = (id, w) => `https://drive.google.com/thumbnail?id=${id}&sz=w${w}`;

// Primary-resource zone: the first two subheadings/topics of a step, where Sean places the key video/image.
const PrimaryCtx = createContext(false);
export const PrimaryZone = ({ on = true, children }) => <PrimaryCtx.Provider value={on}>{children}</PrimaryCtx.Provider>;
export const usePrimaryZone = () => useContext(PrimaryCtx);

// Curated display titles (display only; linked files are unchanged).
const TITLE_OVERRIDES = { "1 official connect": "Connect — Conversation Guide", "official connect": "Connect — Conversation Guide", "connect": "Connect — Conversation Guide", "1 connect": "Connect — Conversation Guide" };
const ACRONYMS = new Set(["PBO", "PBOS", "ATL", "HTP", "CAMHQ", "SEPL2", "90DCR", "ABI", "VES", "DTM", "BP", "AV", "PA", "AU", "NZ", "TBC", "IBO", "PV", "UK", "USA"]);
const QUESTION = /^(what|how|why|who|when|where|which|are|is|do|does|can|should|will)\b/i;

// Friendly display title: drop extensions, "click here" suffixes, underscores, draft/copy clutter; add "?" to questions.
export function resourceTitle(text) {
  const raw = (text || "").trim();
  let t = raw.replace(/\.(png|jpe?g|gif|webp|heic|mp4|mov|m4v|m4a|mp3|wav|aac|pdf|docx?|pptx?|xlsx?|key)$/i, "");
  t = t.replace(/\s*[-–—:|]?\s*(watch|click|open|view|listen)\s+(the\s+)?(video|here|now|link|image)\s*[.!>]*$/i, "");
  t = t.replace(/_/g, " ").replace(/^\s*copy of\s+/i, "").replace(/\s*[([]?\b(draft|final|wip)\b[)\]]?\s*$/i, "").replace(/\s{2,}/g, " ").trim();
  t = t.replace(/\s*[-–—:]?\s*\(?\b(image|video|audio|pdf)\b\)?$/i, "").trim() || t;
  // Internal clutter: the word "Official" and SHOUTED words (acronyms kept); leading sequence numbers are kept.
  t = t.replace(/\bofficial\s+/i, "").replace(/\b[A-Z][A-Z']{1,}\b/g, (w) => (ACRONYMS.has(w) ? w : w.charAt(0) + w.slice(1).toLowerCase()));
  const o = TITLE_OVERRIDES[t.toLowerCase()];
  if (o) return o;
  if (QUESTION.test(t) && !/[?.!]$/.test(t) && t.split(" ").length >= 3) t += "?";
  return t || raw;
}

// Build a resource segment from a plain (text, url) pair, e.g. Bootcamp items.
export function segFromLink(text, url, hint) {
  const id = (url.match(/drive\.google\.com\/(?:file\/d\/|open\?id=)([\w-]+)/) || [])[1];
  const ext = (text.match(/\.(\w{2,4})\s*$/) || [])[1]?.toLowerCase();
  const byExt = { mp3: "audio", m4a: "audio", wav: "audio", aac: "audio", mp4: "video", mov: "video", m4v: "video", png: "image", jpg: "image", jpeg: "image", webp: "image", pdf: "pdf" }[ext];
  const byUrl = /\/document\//.test(url) ? "doc" : /\/spreadsheets\//.test(url) ? "sheet" : /\/presentation\//.test(url) ? "slides"
    : /youtu\.?be/.test(url) ? "video" : /\/drive\/folders\//.test(url) ? "folder" : null;
  return { t: text, u: id ? `https://drive.google.com/file/d/${id}/view` : url, file_id: id || null, kind: byExt || byUrl || (id ? hint || "file" : "link") };
}

function destination(u) {
  if (/docs\.google\.com\/spreadsheets/.test(u)) return "Google Sheets";
  if (/docs\.google\.com\/document/.test(u)) return "Google Docs";
  if (/docs\.google\.com\/presentation/.test(u)) return "Google Slides";
  if (/drive\.google\.com/.test(u)) return "Google Drive";
  if (/youtu\.?be/.test(u)) return "YouTube";
  try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return "website"; }
}

const returnFocus = (ref) => (e) => { e.preventDefault(); ref.current?.focus(); };

function OpenOriginal({ u, linkRef }) {
  return (
    <a ref={linkRef} href={u} target="_blank" rel="noopener noreferrer" data-testid="viewer-open-original"
       className="inline-flex items-center gap-1 min-h-[44px] text-sm font-medium text-[#0A1F44] underline decoration-[#C9A961] underline-offset-4">
      <ExternalLink className="w-4 h-4" /> Open original ({destination(u)})
    </a>
  );
}

function ImageViewer({ seg, title, open, onOpenChange, rowRef }) {
  const [zoom, setZoom] = useState(false);
  const [state, setState] = useState("loading");
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setZoom(false); setState("loading"); } }}>
      <DialogContent onCloseAutoFocus={returnFocus(rowRef)} className="max-w-[96vw] w-[96vw] h-[90vh] p-3 sm:p-4 flex flex-col gap-2" data-testid="vetting-image-viewer">
        <DialogHeader className="pr-10">
          <DialogTitle className="font-serif text-[#0A1F44] text-lg leading-snug">{title}</DialogTitle>
          <DialogDescription className="text-sm text-slate-600">Zoom in to read details, then drag or scroll to move around.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" className="min-h-[44px]" onClick={() => setZoom((z) => !z)} data-testid="vetting-image-zoom">
            {zoom ? <ZoomOut className="w-4 h-4 mr-1" /> : <ZoomIn className="w-4 h-4 mr-1" />}{zoom ? "Fit to screen" : "Zoom in"}
          </Button>
          <OpenOriginal u={seg.u} />
        </div>
        <div className="relative flex-1 overflow-auto rounded-lg bg-slate-50 border border-slate-200 touch-pan-x touch-pan-y">
          {state !== "ok" && (
            <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-slate-700" data-testid="viewer-image-state">
              {state === "loading" ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading image…</> : "This image couldn't load here. Use Open original above."}
            </div>
          )}
          {open && state !== "error" && <img src={thumb(seg.file_id, 3000)} alt={title} onLoad={() => setState("ok")} onError={() => setState("error")}
                        className={zoom ? "max-w-none w-[220%] sm:w-[160%]" : "w-full h-full object-contain"} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PreviewDialog({ seg, title, kind, open, onOpenChange, rowRef }) {
  const linkRef = useRef(null);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Focus the Open original link, not the cross-origin iframe, so Escape keeps working. No autoplay. */}
      <DialogContent onOpenAutoFocus={(e) => { e.preventDefault(); linkRef.current?.focus(); }}
                     onCloseAutoFocus={returnFocus(rowRef)} className="max-w-[96vw] w-[960px] p-3 sm:p-4" data-testid="vetting-preview-dialog">
        <DialogHeader className="pr-10">
          <DialogTitle className="font-serif text-[#0A1F44] text-lg leading-snug">{title}</DialogTitle>
          <DialogDescription className="text-sm text-slate-600">Press play to start. If it doesn't load, use Open original.</DialogDescription>
        </DialogHeader>
        {open && (
          <div className={`relative w-full rounded-lg border border-slate-200 bg-slate-100 ${kind === "audio" ? "h-40" : kind === "pdf" ? "h-[70vh]" : "aspect-video"}`}>
            <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-600"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…</div>
            <iframe title={title} src={`https://drive.google.com/file/d/${seg.file_id}/preview`} allow="fullscreen" className="relative w-full h-full rounded-lg" />
          </div>
        )}
        <OpenOriginal u={seg.u} linkRef={linkRef} />
      </DialogContent>
    </Dialog>
  );
}

const ROW = "group w-full flex items-center gap-3 min-h-[48px] px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-left hover:border-[#C9A961] hover:bg-[#C9A961]/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A961] focus-visible:ring-offset-1";
const PRIMARY = "group w-full flex items-center gap-3 px-3 py-3 rounded-xl border-2 border-[#C9A961] bg-[#FBF8F0] text-left shadow-sm hover:bg-[#C9A961]/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0A1F44] focus-visible:ring-offset-1";

function Thumb({ seg, kind, Icon }) {
  const [ok, setOk] = useState(true);
  if (kind === "image" && seg.file_id && ok) {
    return <img src={thumb(seg.file_id, 160)} alt="" loading="lazy" onError={() => setOk(false)} className="w-14 h-14 rounded-lg object-cover border border-[#C9A961]/40 bg-white shrink-0" />;
  }
  return <span className="w-12 h-12 rounded-full bg-[#0A1F44] text-[#C9A961] flex items-center justify-center shrink-0"><Icon className="w-6 h-6" aria-hidden="true" /></span>;
}

export default function ResourceCard({ seg, testid, primary = false }) {
  const [open, setOpen] = useState(false);
  const rowRef = useRef(null);
  const kind = seg.u?.includes("/drive/folders/") ? "folder" : (seg.kind || "link");
  const Icon = ICONS[kind] || Link2;
  const drive = !!seg.file_id && seg.u.includes("drive.google.com/file");
  const inApp = drive && (kind === "image" || PREVIEWABLE.has(kind));
  const title = resourceTitle(seg.t);
  const action = ACTION[kind] || "Open";
  const where = inApp ? "Opens here" : `Opens ${destination(seg.u)}`;
  const isPrimary = primary && MEDIA.has(kind);
  const inner = isPrimary ? (
    <>
      <Thumb seg={seg} kind={kind} Icon={Icon} />
      <span className="flex-1 min-w-0">
        <span className="block text-base font-semibold text-[#0A1F44] leading-snug break-words">{title}</span>
        <span className="mt-1.5 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-[#0A1F44] text-white text-sm font-semibold px-3 py-1">{action}</span>
          <span className="text-xs font-medium text-slate-700">{KIND_LABEL[kind]} · {where}</span>
        </span>
      </span>
    </>
  ) : (
    <>
      <Icon className="w-5 h-5 text-[#8A6D1F] shrink-0" aria-hidden="true" />
      <span className="flex-1 min-w-0">
        <span className="block text-[15px] sm:text-sm font-medium text-[#0A1F44] leading-snug break-words">{title}</span>
        {!inApp && <span className="block text-xs text-slate-600 mt-0.5">{where}</span>}
      </span>
      <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-700 bg-slate-100 rounded px-1.5 py-0.5 shrink-0">{KIND_LABEL[kind] || "Link"}</span>
      {inApp ? <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" aria-hidden="true" /> : <ExternalLink className="w-4 h-4 text-slate-500 shrink-0" aria-hidden="true" />}
    </>
  );
  const cls = isPrimary ? PRIMARY : ROW;
  const aria = `${action}: ${title} (${KIND_LABEL[kind] || "Link"}${inApp ? "" : `, opens ${destination(seg.u)} in a new tab`})`;
  return (
    <div data-testid={testid} data-primary={isPrimary ? "true" : undefined}>
      {inApp ? (
        <button ref={rowRef} type="button" className={cls} onClick={() => setOpen(true)} aria-haspopup="dialog" data-testid={`${testid}-open`} aria-label={aria}>{inner}</button>
      ) : (
        <a href={seg.u} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`${testid}-open`} aria-label={aria}>{inner}</a>
      )}
      {inApp && kind === "image" && <ImageViewer seg={seg} title={title} open={open} onOpenChange={setOpen} rowRef={rowRef} />}
      {inApp && kind !== "image" && <PreviewDialog seg={seg} title={title} kind={kind} open={open} onOpenChange={setOpen} rowRef={rowRef} />}
    </div>
  );
}

// In a primary zone, the first video/audio and the first image of a block are the key resources.
export function primaryFlags(segs, zone) {
  const seen = new Set();
  return segs.map((s) => {
    if (!zone) return false;
    const k = s.kind === "audio" ? "video" : s.kind;
    if (!MEDIA.has(s.kind) || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}
